import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';
import {
  invalidateAfterRsvpChange,
  afterMatchRpc,
  invalidateAfterGroupMembershipChange,
} from '../lib/query/invalidateMutations';

import { stashPendingAction } from '../lib/pendingAction';
import { openFestivalPicks } from '../lib/festivalPicks';
import {
  useRsvpWrite,
  runRsvpStatusWrite,
  runDeleteRsvpWrite,
  isRsvpMutationQueuedError,
} from './mutations/useRsvpWrite';
import { useGroupMembershipWrite } from './mutations/useGroupMembershipWrite';
import GoingOptionsModal from '../components/rsvp/GoingOptionsModal';
import { useFestivalMatchSetup } from './useFestivalMatchSetup';
import RejoinModal from '../components/rsvp/RejoinModal';
import UnRsvpConfirm from '../components/rsvp/UnRsvpConfirm';
import EditRsvpSheet from '../components/rsvp/EditRsvpSheet';
import { classifyMatchUserToGroupPayload } from '../utils/matchJoinOutcomes';
import { usePromptAuth } from '../components/auth/AuthPrompt';
import { resolveEventDisplayName } from '../utils/eventDisplayName';

/**
 * Same RSVP / group / queue / rejoin behavior as EventsList for EventCard parity.
 */
export function useEventRsvpParity({
  session,
  showToast,
  getEventForCutoff,
  updateLocalRsvp,
  refetchRsvps,
}) {
  const navigate = useNavigate();
  const promptAuth = usePromptAuth();
  // Signed out on a card: the sign-in pop-up, named for the event; signed in, the event page finishes the stashed RSVP
  // (its pending-action effect). This one used to go to the login page with no way back, and the RSVP was lost.
  const askToJoin = async (eventId) => {
    stashPendingAction({ type: 'rsvp_going', eventId });
    let name = null;
    try {
      name = resolveEventDisplayName(await getEventForCutoff?.(eventId));
    } catch {
      /* the title can be plain */
    }
    promptAuth({
      action: 'Join',
      target: name || 'this event',
      onSuccess: () => navigate(`/events/${eventId}`),
    });
  };
  const queryClient = useQueryClient();
  const rsvpWrite = useRsvpWrite(session?.user?.id);
  const groupMembershipWrite = useGroupMembershipWrite(session?.user?.id);
  const { maybeInterceptFestivalQueue, festivalSetupModal } = useFestivalMatchSetup(
    session?.user?.id,
  );
  const [, setRsvpLoadingEventId] = useState(null);
  const [editRsvpEventId, setEditRsvpEventId] = useState(null);
  const [confirmUnRSVPModal, setConfirmUnRSVPModal] = useState({
    show: false,
    eventId: null,
    groups: [],
    hasQueueEntry: false,
  });
  const [goingOptionsModal, setGoingOptionsModal] = useState({ show: false, eventId: null });
  const [rejoinModal, setRejoinModal] = useState({
    show: false,
    group: null,
    eventId: null,
    skippedGroupIds: [],
    remainingCandidates: [],
  });

  const getRsvpToastMessage = () =>
    "You're looking for a group! We'll match you as soon as others join.";

  const syncRsvpCaches = async (eventId, groupId) => {
    await refetchRsvps?.();
    invalidateAfterRsvpChange(queryClient, { userId: session?.user?.id, eventId });
    if (groupId) {
      invalidateAfterGroupMembershipChange(queryClient, {
        userId: session?.user?.id,
        groupId,
        eventId,
      });
    }
  };

  const handleRejoinGroup = async () => {
    const { group, eventId, skippedGroupIds } = rejoinModal;
    if (!group || !eventId) return;
    setRejoinModal({
      show: false,
      group: null,
      eventId: null,
      skippedGroupIds: [],
      remainingCandidates: [],
    });

    const { data, error } = await supabase.rpc('confirm_rejoin_group', {
      p_group_id: group.group_id,
    });
    if (error) {
      console.error('[EventRsvpParity] confirm_rejoin_group error:', error);
      showToast('Failed to rejoin group.');
      return;
    }
    afterMatchRpc(queryClient, { userId: session?.user?.id, eventId, groupId: data?.group_id }); // the RPC is the RSVP here: refresh Maps' list and start the map
    if (data.outcome === 'rejoined') {
      showToast("You've rejoined the group!");
      navigate(`/groups/${data.group_id}`);
    } else if (data.outcome === 'full') {
      showToast('This group is now full. Finding another group...');
      const skipIds = [...(skippedGroupIds || []), group.group_id];
      const { data: matchData, error: matchErr } = await supabase.rpc('match_user_to_group', {
        p_event_id: eventId,
        p_skip_group_ids: skipIds,
      });
      if (matchErr) {
        showToast('Could not find a group.');
        return;
      }
      afterMatchRpc(queryClient, {
        userId: session?.user?.id,
        eventId,
        groupId: matchData?.group_id,
      }); // the RPC is the RSVP here: refresh Maps' list and start the map
      if (matchData.outcome === 'matched' || matchData.outcome === 'already_member') {
        showToast("You've been matched into a group!");
        navigate(`/groups/${matchData.group_id}`);
      } else if (matchData.outcome === 'queued') {
        showToast(getRsvpToastMessage());
      } else if (matchData.outcome === 'rejoin_available' && matchData.candidates?.length) {
        setRejoinModal({
          show: true,
          group: matchData.candidates[0],
          eventId,
          skippedGroupIds: skipIds,
          remainingCandidates: matchData.candidates.slice(1),
        });
      } else if (matchData.outcome === 'closed') {
        showToast('Matching is closed for this event.');
      } else if (matchData.outcome === 'error') {
        showToast(matchData.message || 'Could not find a group.');
      }
    } else {
      showToast(data.message || 'Could not rejoin group.');
    }
  };

  const handleSkipRejoinGroup = async () => {
    const { group, eventId, skippedGroupIds, remainingCandidates } = rejoinModal;
    if (!group || !eventId) return;

    const newSkippedIds = [...skippedGroupIds, group.group_id];

    const nextCandidate = remainingCandidates.find((c) => !newSkippedIds.includes(c.group_id));

    if (nextCandidate) {
      setRejoinModal({
        show: true,
        group: nextCandidate,
        eventId,
        skippedGroupIds: newSkippedIds,
        remainingCandidates: remainingCandidates.filter(
          (c) => c.group_id !== nextCandidate.group_id,
        ),
      });
      return;
    }

    setRejoinModal({
      show: false,
      group: null,
      eventId: null,
      skippedGroupIds: [],
      remainingCandidates: [],
    });

    const { data, error } = await supabase.rpc('match_user_to_group', {
      p_event_id: eventId,
      p_skip_group_ids: newSkippedIds,
    });
    if (error) {
      showToast('Could not find a group.');
      return;
    }
    afterMatchRpc(queryClient, { userId: session?.user?.id, eventId, groupId: data?.group_id }); // the RPC is the RSVP here: refresh Maps' list and start the map

    if (data.outcome === 'matched' || data.outcome === 'already_member') {
      showToast("You're matched into a group.");
      navigate(`/groups/${data.group_id}`);
    } else if (data.outcome === 'queued') {
      showToast(getRsvpToastMessage());
    } else if (data.outcome === 'closed') {
      showToast('Matching is closed for this event.');
    } else if (data.outcome === 'rejoin_available' && data.candidates?.length) {
      setRejoinModal({
        show: true,
        group: data.candidates[0],
        eventId,
        skippedGroupIds: newSkippedIds,
        remainingCandidates: data.candidates.slice(1),
      });
    }
  };

  const setRsvpStatusForEvent = async (
    eventId,
    status,
    goingChoice = 'queue',
    previousStatus = null,
    groupName = null,
  ) =>
    runRsvpStatusWrite({
      rsvpWrite,
      userId: session.user.id,
      eventId,
      status,
      goingChoice,
      previousStatus,
      groupName,
      getEventForCutoff,
      showToast,
      navigate,
      setRejoinModal,
      getRsvpToastMessage,
    });

  const deleteRsvpForEvent = async (eventId, previousStatus = null) =>
    runDeleteRsvpWrite({
      rsvpWrite,
      userId: session.user.id,
      eventId,
      previousStatus,
    });

  const checkGroupsBeforeUnRSVP = async (eventId) => {
    const { data: userGroupsData, error: groupsError } = await supabase
      .from('group_members')
      .select(
        `
        group_id,
        groups!inner(
          id,
          name,
          event_id
        )
      `,
      )
      .eq('user_id', session.user.id)
      .eq('groups.event_id', eventId)
      .is('left_at', null);

    if (groupsError) {
      console.error('[EventRsvpParity] Error checking groups:', groupsError);
    }

    const { data: queueData, error: queueError } = await supabase
      .from('group_matching_queue')
      .select('id, status')
      .eq('user_id', session.user.id)
      .eq('event_id', eventId);

    if (queueError) {
      console.error('[EventRsvpParity] Error checking queue:', queueError);
    }

    const hasGroups = userGroupsData && userGroupsData.length > 0;
    const hasQueueEntry = queueData && queueData.length > 0;

    if (hasGroups || hasQueueEntry) {
      setConfirmUnRSVPModal({
        show: true,
        eventId,
        groups: userGroupsData || [],
        hasQueueEntry,
      });
      return true;
    }

    return false;
  };

  const handleConfirmUnRSVP = async () => {
    const { eventId, groups } = confirmUnRSVPModal;

    try {
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', session.user.id)
        .single();

      const displayName = userProfile?.display_name || 'Someone';

      if (groups.length > 0) {
        for (const g of groups) {
          await groupMembershipWrite.mutateAsync({
            action: 'leave',
            groupId: g.group_id,
            userId: session.user.id,
            displayName,
            groupName: g.groups?.name,
            postSystemMessage: true,
          });
        }
      }

      await deleteRsvpForEvent(eventId, 'going');
      if (eventId) {
        localStorage.removeItem(`dismissed_group_prompt_${eventId}`);
      }
      updateLocalRsvp(eventId, null);

      setConfirmUnRSVPModal({ show: false, eventId: null, groups: [], hasQueueEntry: false });
      showToast("You're no longer marked as going.");
      await syncRsvpCaches(eventId);
    } catch (err) {
      console.error('[EventRsvpParity] Error confirming un-RSVP:', err);
    }
  };

  const handleCardGoing = async (eventId, myStatus, e, confirmed = false) => {
    e.stopPropagation();
    if (!session) {
      await askToJoin(eventId);
      return;
    }
    // Going opens Edit RSVP first (Sam's B, 2026-11-04); its Not going comes back here confirmed.
    if (myStatus === 'going' && !confirmed) {
      setEditRsvpEventId(eventId);
      return;
    }
    setRsvpLoadingEventId(eventId);
    try {
      if (myStatus === 'going') {
        const hasGroups = await checkGroupsBeforeUnRSVP(eventId);
        if (hasGroups) {
          setRsvpLoadingEventId(null);
          return;
        }
        localStorage.removeItem(`dismissed_group_prompt_${eventId}`);
        await deleteRsvpForEvent(eventId, 'going');
        updateLocalRsvp(eventId, null);
        showToast("You're no longer marked as going.");
      } else {
        setGoingOptionsModal({ show: true, eventId });
        return;
      }
    } finally {
      setRsvpLoadingEventId(null);
    }
  };

  const closeGoingOptionsModal = () => {
    setGoingOptionsModal({ show: false, eventId: null });
  };

  const handleGoingOption = async (choice, groupName = null) => {
    const eventId = goingOptionsModal.eventId;
    if (!eventId) return;
    setGoingOptionsModal({ show: false, eventId: null });

    // Festivals collect attendance days + lineup votes before queueing.
    if (
      choice === 'queue' &&
      (await maybeInterceptFestivalQueue(eventId, () => executeGoingOption(eventId, choice)))
    ) {
      return;
    }

    await executeGoingOption(eventId, choice, groupName);
  };

  const executeGoingOption = async (eventId, choice, groupName = null) => {
    setRsvpLoadingEventId(eventId);
    try {
      // On the tap, not after the round trip (2026-10-27 latency audit); a refusal still gets its toast from the catch.
      if (choice === 'skip') showToast("You're going");
      const toastMessage = await setRsvpStatusForEvent(eventId, 'going', choice, null, groupName);
      updateLocalRsvp(eventId, 'going');
      if (choice !== 'skip' && toastMessage) {
        showToast(toastMessage);
      }
      await syncRsvpCaches(eventId);
      // Just me on a festival: its picks come next (a new group opens the group; the queue took picks in its modal).
      if (choice === 'skip') void openFestivalPicks({ queryClient, navigate, eventId });
    } catch (err) {
      if (isRsvpMutationQueuedError(err)) {
        showToast(err.message);
        return;
      }
      console.error('[EventRsvpParity] RSVP error:', err);
      showToast(err?.message || 'Could not update RSVP.');
    } finally {
      setRsvpLoadingEventId(null);
      setGoingOptionsModal({ show: false, eventId: null });
    }
  };

  const handleJoinExistingGroup = async (groupId) => {
    const eventId = goingOptionsModal.eventId;
    if (!eventId || !groupId) return;
    setGoingOptionsModal({ show: false, eventId: null });
    setRsvpLoadingEventId(eventId);
    try {
      await setRsvpStatusForEvent(eventId, 'going', 'skip');
      updateLocalRsvp(eventId, 'going');
      await syncRsvpCaches(eventId, groupId);
      stashPendingAction({ type: 'join_group_chat', groupId });
      navigate(`/groups/${groupId}`);
    } catch (err) {
      console.error('[EventRsvpParity] join existing group error:', err);
      showToast(err?.message || 'Could not join group.');
    } finally {
      setRsvpLoadingEventId(null);
    }
  };

  const handleEventCardRSVP = async (eventId, nextStatus, currentMyStatus) => {
    if (!session) {
      await askToJoin(eventId);
      return;
    }
    const mockEvent = { stopPropagation: () => {} };
    if (nextStatus === 'going') {
      await handleCardGoing(eventId, currentMyStatus, mockEvent);
    } else if (nextStatus === null && currentMyStatus === 'going') {
      await handleCardGoing(eventId, currentMyStatus, mockEvent);
    }
  };

  /**
   * One-tap public match (e.g. Chats groupless rows): server-side match_user_to_group.
   */
  const joinPublicMatchQueue = async (eventId) => {
    if (!session?.user?.id) return { kind: 'no_session' };
    try {
      const { data, error } = await supabase.rpc('match_user_to_group', {
        p_event_id: eventId,
        p_skip_group_ids: [],
      });
      if (error) return { kind: 'error', error };
      afterMatchRpc(queryClient, { userId: session?.user?.id, eventId, groupId: data?.group_id });

      const classified = classifyMatchUserToGroupPayload(data);
      switch (classified.kind) {
        case 'matched':
          return { kind: 'matched', groupId: classified.groupId };
        case 'rejoin_available':
          if (classified.candidates.length > 0) {
            setRejoinModal({
              show: true,
              group: classified.candidates[0],
              eventId,
              skippedGroupIds: [],
              remainingCandidates: classified.candidates.slice(1),
            });
          }
          return { kind: 'rejoin' };
        case 'queued':
          return { kind: 'queued' };
        case 'closed':
          return { kind: 'closed' };
        case 'rpc_error':
          return { kind: 'error', error: new Error(classified.message || 'Matching failed') };
        default:
          return { kind: 'error', error: new Error(data?.message || 'Unknown outcome') };
      }
    } catch (err) {
      return { kind: 'error', error: err };
    }
  };

  const resetConfirmUnRSVPModal = () =>
    setConfirmUnRSVPModal({ show: false, eventId: null, groups: [], hasQueueEntry: false });

  const parityModals = (
    <>
      <EditRsvpSheet
        isOpen={editRsvpEventId != null}
        onClose={() => setEditRsvpEventId(null)}
        onNotGoing={() => {
          const eventId = editRsvpEventId;
          setEditRsvpEventId(null);
          handleCardGoing(eventId, 'going', { stopPropagation: () => {} }, true);
        }}
      />
      <GoingOptionsModal
        isOpen={goingOptionsModal.show}
        onClose={closeGoingOptionsModal}
        onMatchMe={() => handleGoingOption('queue')}
        onCreateGroup={(name) => handleGoingOption('create', name)}
        onSkip={() => handleGoingOption('skip')}
        onJoinExistingGroup={handleJoinExistingGroup}
        eventId={goingOptionsModal.eventId ?? undefined}
      />

      {festivalSetupModal}

      <RejoinModal
        isOpen={rejoinModal.show}
        onClose={() =>
          setRejoinModal({
            show: false,
            group: null,
            eventId: null,
            skippedGroupIds: [],
            remainingCandidates: [],
          })
        }
        onRejoin={handleRejoinGroup}
        onFindDifferent={handleSkipRejoinGroup}
        groupName={rejoinModal.group?.group_name}
      />

      <UnRsvpConfirm
        isOpen={confirmUnRSVPModal.show}
        onClose={resetConfirmUnRSVPModal}
        onConfirm={handleConfirmUnRSVP}
        groups={confirmUnRSVPModal.groups}
        hasQueueEntry={confirmUnRSVPModal.hasQueueEntry}
      />
    </>
  );

  return { handleEventCardRSVP, parityModals, joinPublicMatchQueue, maybeInterceptFestivalQueue };
}
